import { TextInput } from '@mantine/core';
import { useEffect, useMemo } from 'react';
import { useFillOutRevisionDataProvider } from 'components/forms/revision/fill-out/data/FillOutRevisionDataProvider';
import { RevisionFieldProps } from 'components/forms/revision/fill-out/types';
import { IFormInputSpecIfElse } from 'pages/revisions-module/template-editor/editors/form/types';

/**
 * Field used to resolve value by condition:
 * val(fieldSource) ? val(fieldTrue) : val(fieldFalse)
 */
export default function RevisionFieldIfElse({ spec, name, deviceId }: RevisionFieldProps<IFormInputSpecIfElse>) {
  const { form, findInputByName } = useFillOutRevisionDataProvider();
  const sourceInput = useMemo(() => findInputByName(spec.fieldSource), [findInputByName, spec.fieldSource]);
  const trueInput = useMemo(() => findInputByName(spec.fieldTrue), [findInputByName, spec.fieldTrue]);
  const falseInput = useMemo(() => findInputByName(spec.fieldFalse), [findInputByName, spec.fieldFalse]);
  const inputProps = form.getInputProps(name);

  /** Returns the source field value according to its form context. */
  const getInputValue = (inputName: string, context?: 'revision' | 'device') => {
    if (!inputName) {
      return undefined;
    }

    if (context === 'revision') {
      return form.values.fields[inputName];
    }

    if (context === 'device' && deviceId !== undefined) {
      return form.values.devices[String(deviceId)]?.fields?.[inputName];
    }

    return undefined;
  };

  const sourceValue = getInputValue(spec.fieldSource, sourceInput?.context as 'revision' | 'device' | undefined);
  const trueValue = getInputValue(spec.fieldTrue, trueInput?.context as 'revision' | 'device' | undefined);
  const falseValue = getInputValue(spec.fieldFalse, falseInput?.context as 'revision' | 'device' | undefined);

  const resolvedRawValue = sourceValue ? trueValue : falseValue;
  const resolvedValue = resolvedRawValue === undefined || resolvedRawValue === null ? '' : String(resolvedRawValue);

  useEffect(() => {
    const currentValue = inputProps.value;

    if (currentValue !== resolvedValue) {
      form.setFieldValue(name, resolvedValue);
    }
  }, [form, name, resolvedValue, inputProps.value]);

  return (
    <TextInput
      size="lg"
      name={name}
      label={spec.label}
      required={spec.required}
      {...inputProps}
      value={resolvedValue}
      readOnly
    />
  );
}
